import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Search from './pages/Search';
import Report from './pages/Report';

// Protected Route wrapper
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-400 font-body">Loading Classify...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <AppLayout /> : <Navigate to="/login" />;
}

// Main app layout with nav bar
function AppLayout() {
  const { user, tenant, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <nav className="bg-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left: Logo + Nav */}
            <div className="flex items-center gap-6">
              <Link to="/search" className="flex items-center gap-2">
                <img src="/understudy_icon.png" alt="Understudy" className="h-8 w-8" />
                <span className="font-heading text-lg text-white">Understudy</span>
                <span className="text-teal text-xs font-semibold tracking-wider">CLASSIFY</span>
              </Link>
              <Link to="/search" className="text-sm text-navy-200 hover:text-white transition-colors">
                Search
              </Link>
            </div>

            {/* Right: User info + Actions */}
            <div className="flex items-center gap-4">
              {tenant && (
                <span className="text-xs text-navy-200 hidden sm:block">
                  {tenant.name} • {tenant.searches_used}/{tenant.searches_limit} searches
                </span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-navy-100">{user?.name}</span>
                {isSuperAdmin && (
                  <span className="text-[10px] bg-purple px-1.5 py-0.5 rounded text-white font-bold">ADMIN</span>
                )}
              </div>
              <button 
                onClick={handleLogout}
                className="text-sm text-navy-200 hover:text-white transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            className: 'toast-custom',
            duration: 4000,
            style: { fontFamily: 'Open Sans, sans-serif' }
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginGuard />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/search" element={<Search />} />
            <Route path="/report/:id" element={<Report />} />
          </Route>
          <Route path="*" element={<Navigate to="/search" />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Redirect authenticated users away from login
function LoginGuard() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <Navigate to="/search" /> : <Login />;
}

export default App;
